/**
 * DensityProvider — global density context (Compact / Cozy / Comfortable).
 *
 * Pattern from Datadog, Linear, Dynamics 365, Outlook. Operators triaging
 * 8k stations want compact (28px rows); casual users prefer comfortable
 * (48px). Persisted to localStorage so the choice survives sessions.
 *
 * Wrap the Workbench shell once with `<DensityProvider>`, then call
 * `useDensity()` inside any toolkit component to read the row height,
 * gap scale, and font scale tokens.
 *
 * For per-app overrides, nest a second `<DensityProvider value="compact">`
 * — the closest ancestor wins.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DENSITY_TOKENS,
  DensityContext,
  type Density,
  type DensityContextValue,
} from './densityContext';

const STORAGE_KEY = 'walle.workbench.density';

interface DensityProviderProps {
  /** Default density when no localStorage value exists. */
  defaultDensity?: Density;
  /** Force a specific density (no persistence). For per-app override. */
  value?: Density;
  children: ReactNode;
}

export function DensityProvider({ defaultDensity = 'cozy', value, children }: DensityProviderProps) {
  const [stored, setStored] = useState<Density>(() => {
    if (value) return value;
    if (typeof window === 'undefined') return defaultDensity;
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'compact' || v === 'cozy' || v === 'comfortable' ? v : defaultDensity;
  });

  const density = value ?? stored;

  const setDensity = useCallback((d: Density) => {
    setStored(d);
    if (typeof window !== 'undefined' && !value) {
      window.localStorage.setItem(STORAGE_KEY, d);
    }
  }, [value]);

  /* Apply CSS custom properties to :root so non-React styles can read them too. */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const t = DENSITY_TOKENS[density];
    const root = document.documentElement;
    root.style.setProperty('--walle-density-row-h', t.rowHeight);
    root.style.setProperty('--walle-density-btn-h', t.buttonHeight);
    root.style.setProperty('--walle-density-font-body', t.fontSizeBody);
    root.style.setProperty('--walle-density-spacing-y', t.spacingY);
    root.dataset.density = density;
  }, [density]);

  const ctx = useMemo<DensityContextValue>(
    () => ({ density, tokens: DENSITY_TOKENS[density], setDensity }),
    [density, setDensity],
  );

  return <DensityContext.Provider value={ctx}>{children}</DensityContext.Provider>;
}
